import { AlertTriangle, X } from "lucide-react";

/**
 * Reusable confirmation modal with a polished look, replacing window.confirm().
 *
 * Props:
 *  - open        : boolean — whether the modal is visible
 *  - title       : string  — heading
 *  - message     : node    — body text / description
 *  - confirmLabel: string  — confirm button text (default "Confirmer")
 *  - cancelLabel : string  — cancel button text (default "Annuler")
 *  - onConfirm   : fn      — called when confirm is clicked
 *  - onCancel    : fn      — called when cancel / backdrop / X is clicked
 *  - loading     : boolean — shows a spinner and disables actions
 *  - variant     : "danger" | "primary" (default "danger")
 */
export default function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  onConfirm,
  onCancel,
  loading = false,
  variant = "danger",
}) {
  if (!open) return null;

  const accent =
    variant === "danger"
      ? {
          ring: "bg-red-100",
          icon: "text-red-600",
          btn: "bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 focus:ring-red-300",
        }
      : {
          ring: "bg-indigo-100",
          icon: "text-indigo-600",
          btn: "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:ring-indigo-300",
        };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-[fadeIn_0.15s_ease-out]"
      onClick={loading ? undefined : onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all animate-[popIn_0.18s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <div className="relative">
          <button
            onClick={onCancel}
            disabled={loading}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-7 pb-5 text-center">
          <div
            className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${accent.ring}`}
          >
            <AlertTriangle className={`h-7 w-7 ${accent.icon}`} />
          </div>
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          {message && (
            <div className="mt-2 text-sm text-gray-500 leading-relaxed">
              {message}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all focus:outline-none focus:ring-4 disabled:opacity-70 ${accent.btn} flex items-center justify-center gap-2`}
          >
            {loading && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes popIn { from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      `}</style>
    </div>
  );
}
